/**
 * ManagementPage Component
 *
 * Main page for managing clients and pending classifications.
 * Provides tabs for switching between Clients and Pending Classifications views.
 */

import { useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/components/Auth';
import { Layout } from '@/components/Layout';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { Badge } from '@/components/ui/badge';
import { useClients } from '@/hooks/useClients';
import { usePendingClassifications } from '@/hooks/usePendingClassifications';
import { useSyncJobs } from '@/hooks/useSyncJobs';
import { ClientsTable } from './ClientsTable';
import { ClientFormDialog } from './ClientFormDialog';
import { DeleteClientDialog } from './DeleteClientDialog';
import { DocumentsTab } from './DocumentsTab';
import { SyncJobsTab } from './SyncJobsTab';
import type { Client, ClientFormData } from '@/types/management';
import { toast } from 'sonner';

type ManagementTab = 'clients' | 'documents' | 'jobs';

export function ManagementPage() {
  const { tab, clientSlug } = useParams<{
    tab?: string;
    clientSlug?: string;
  }>();
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const {
    clients,
    isLoading: isLoadingClients,
    error: clientsError,
    hasFetched: clientsHasFetched,
    createClient,
    updateClient,
    deleteClient,
  } = useClients();

  const {
    pending,
    isLoading: isLoadingPending,
    error: pendingError,
    hasFetched: pendingHasFetched,
    refetch: refetchPending,
    resolve: resolvePending,
    dismiss: dismissPending,
  } = usePendingClassifications();

  const {
    syncs,
    isLoading: isLoadingSyncs,
    error: syncsError,
    startSync,
    refetch: refetchSyncs,
    lastUpdated: syncsLastUpdated,
  } = useSyncJobs();

  // Derive active view from URL, defaulting to 'clients'
  // clientSlug being present means we're viewing a specific client's documents
  const activeView: ManagementTab =
    clientSlug || tab === 'documents'
      ? 'documents'
      : tab === 'jobs'
        ? 'jobs'
        : 'clients';

  const handleTabChange = useCallback(
    (value: string) => {
      if (value) {
        navigate(`/management/${value}`);
      }
    },
    [navigate],
  );

  // Dialog state
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);

  // Open form dialog for creating a new client
  const handleAdd = useCallback(() => {
    setSelectedClient(null);
    setFormDialogOpen(true);
  }, []);

  // Open form dialog for editing an existing client
  const handleEdit = useCallback((client: Client) => {
    setSelectedClient(client);
    setFormDialogOpen(true);
  }, []);

  // Open delete confirmation dialog
  const handleDeleteClick = useCallback((client: Client) => {
    setSelectedClient(client);
    setDeleteDialogOpen(true);
  }, []);

  // Handle form submission (create or update)
  const handleFormSubmit = useCallback(
    async (data: ClientFormData) => {
      if (selectedClient) {
        await updateClient(selectedClient.id, data);
        toast.success(`Client "${data.name}" updated successfully`);
      } else {
        await createClient(data);
        toast.success(`Client "${data.name}" created successfully`);
      }
    },
    [selectedClient, createClient, updateClient],
  );

  // Handle delete confirmation
  const handleDeleteConfirm = useCallback(async () => {
    if (selectedClient) {
      await deleteClient(selectedClient.id);
      toast.success(`Client "${selectedClient.name}" deleted`);
    }
  }, [selectedClient, deleteClient]);

  // Handle pending classification resolution
  const handleResolve = useCallback(
    async (id: string, clientId: string) => {
      await resolvePending(id, { clientId });
      toast.success('Document assigned to client');
    },
    [resolvePending],
  );

  // Handle pending classification dismissal
  const handleDismiss = useCallback(
    async (id: string) => {
      await dismissPending(id);
      toast.success('Classification dismissed');
    },
    [dismissPending],
  );

  // Handle document unassignment (refresh pending list)
  const handleDocumentUnassigned = useCallback(() => {
    refetchPending();
    toast.success('Document moved to pending classification');
  }, [refetchPending]);

  return (
    <Layout userEmail={user?.email} onSignOut={signOut}>
      <div className="flex-1 overflow-auto p-4 md:p-6">
        <div className="max-w-7xl mx-auto space-y-6">
          {/* Header */}
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-2xl font-bold tracking-tight">Management</h1>
              <p className="text-sm text-muted-foreground mt-1">
                Configure clients, review documents, and monitor sync jobs
              </p>
            </div>
            <ToggleGroup
              type="single"
              value={activeView}
              onValueChange={handleTabChange}
              variant="outline"
            >
              <ToggleGroupItem value="clients">Clients</ToggleGroupItem>
              <ToggleGroupItem value="documents" className="gap-2">
                Documents
                {pending.length > 0 && (
                  <Badge
                    variant="secondary"
                    className="ml-1 h-5 min-w-[20px] px-1.5"
                  >
                    {pending.length}
                  </Badge>
                )}
              </ToggleGroupItem>
              <ToggleGroupItem value="jobs">Jobs</ToggleGroupItem>
            </ToggleGroup>
          </div>

          {/* Content */}
          <div className="mt-4">
            {activeView === 'clients' && (
              <>
                {/* Error Display */}
                {clientsError && (
                  <div
                    className="p-4 mb-4 bg-destructive/10 border border-destructive/20 rounded-lg"
                    role="alert"
                  >
                    <p className="text-sm text-destructive">{clientsError}</p>
                  </div>
                )}

                {/* Clients Table */}
                <ClientsTable
                  clients={clients}
                  isLoading={isLoadingClients}
                  hasFetched={clientsHasFetched}
                  onEdit={handleEdit}
                  onDelete={handleDeleteClick}
                  onAdd={handleAdd}
                />
              </>
            )}

            {activeView === 'documents' && (
              <DocumentsTab
                clients={clients}
                pending={pending}
                isLoadingPending={isLoadingPending}
                pendingHasFetched={pendingHasFetched}
                pendingError={pendingError}
                onResolve={handleResolve}
                onDismiss={handleDismiss}
                onDocumentUnassigned={handleDocumentUnassigned}
                clientSlug={clientSlug}
              />
            )}

            {activeView === 'jobs' && (
              <SyncJobsTab
                syncs={syncs}
                isLoading={isLoadingSyncs}
                error={syncsError}
                startSync={startSync}
                refetch={refetchSyncs}
                lastUpdated={syncsLastUpdated}
              />
            )}
          </div>
        </div>
      </div>

      {/* Form Dialog (Create/Edit) */}
      <ClientFormDialog
        open={formDialogOpen}
        onOpenChange={setFormDialogOpen}
        client={selectedClient}
        onSubmit={handleFormSubmit}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteClientDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        client={selectedClient}
        onConfirm={handleDeleteConfirm}
      />
    </Layout>
  );
}
